// components/transfers/TransferDetails.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTransferDetails } from '../../services/transferService';
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Copy, 
  Download, 
  ExternalLink,
  User,
  Building,
  BanknoteIcon,
  Calendar,
  FileText,
  Info,
  RefreshCw
} from 'lucide-react';

// Interface for transfer details
interface TransferDetailsType {
  reference: string | null;
  amount: number;
  currency: string;
  source_method: string;
  target_method: string | null;
  status: 'SUCCESS' | 'PENDING' | 'REJECTED' | 'PROCESSING' | 'FAILED' | 'APPROVED';
  created_at: string;
  merchant_code: string;
  comment?: string;
  transfer_type?: string;
  method?: string;
  source_method?: string;
  target_method?: string;
  initiated_by?: string;
  approved_by?: string;
  updated_at?: string;
  merchant_type?: string;
  id?: string | number;
}

const TransferDetails: React.FC = () => {
  const { reference } = useParams<{ reference: string }>();
  const navigate = useNavigate();
  const [transfer, setTransfer] = useState<TransferDetailsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (reference) {
      fetchTransferDetails();
    }
  }, [reference]);

  const fetchTransferDetails = async () => {
    try {
      setLoading(true);
      const data = await getTransferDetails(reference!);
      setTransfer(data);
    } catch (error) {
      console.error('Error fetching details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTransferDetails();
    setRefreshing(false);
  };

  const getStatusConfig = (status: string) => {
    const statusUpper = status?.toUpperCase() || '';
    switch (statusUpper) {
      case 'SUCCESS':
      case 'APPROVED':
        return {
          bg: 'bg-emerald-500/20',
          text: 'text-emerald-400',
          border: 'border-emerald-400/30',
          icon: <CheckCircle className="h-5 w-5" />
        };
      case 'PENDING':
      case 'PROCESSING':
        return {
          bg: 'bg-amber-500/20',
          text: 'text-amber-400',
          border: 'border-amber-400/30',
          icon: <Clock className="h-5 w-5" />
        };
      case 'REJECTED':
      case 'FAILED':
        return {
          bg: 'bg-rose-500/20',
          text: 'text-rose-400',
          border: 'border-rose-400/30',
          icon: <XCircle className="h-5 w-5" />
        };
      default:
        return {
          bg: 'bg-slate-500/20',
          text: 'text-slate-400',
          border: 'border-slate-400/30',
          icon: <Info className="h-5 w-5" />
        };
    }
  };

  const getTypeConfig = (type: string = '') => {
    const configs: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      'wallet_to_wallet': {
        label: 'Wallet → Wallet',
        color: 'bg-purple-500/20 text-purple-400',
        icon: <BanknoteIcon className="h-4 w-4" />
      },
      'wallet_to_bank': {
        label: 'Wallet → Bank',
        color: 'bg-blue-500/20 text-blue-400',
        icon: <Building className="h-4 w-4" />
      },
      'bank_to_wallet': {
        label: 'Bank → Wallet',
        color: 'bg-cyan-500/20 text-cyan-400',
        icon: <BanknoteIcon className="h-4 w-4" />
      },
      'bank_to_bank': {
        label: 'Bank → Bank',
        color: 'bg-indigo-500/20 text-indigo-400',
        icon: <Building className="h-4 w-4" />
      },
      'inter_operator': {
        label: 'Inter-Operator',
        color: 'bg-orange-500/20 text-orange-400',
        icon: <RefreshCw className="h-4 w-4" />
      }
    };
    return configs[type] || { 
      label: type || 'Unknown', 
      color: 'bg-gray-500/20 text-gray-400', 
      icon: <FileText className="h-4 w-4" /> 
    };
  };

  const getOperatorConfig = (operator: string = '') => {
    const operatorLower = operator?.toLowerCase() || '';
    const configs: Record<string, { label: string; color: string; bgColor: string }> = {
      'orange': { label: 'Orange Money', color: '#FF8C00', bgColor: 'bg-orange-500' },
      'airtel': { label: 'Airtel Money', color: '#E53E3E', bgColor: 'bg-red-500' },
      'afrimoney': { label: 'Afrimoney', color: '#000065', bgColor: 'bg-indigo-600' },
      'mpesa': { label: 'M-Pesa', color: '#00A651', bgColor: 'bg-green-600' }
    };
    return configs[operatorLower] || { 
      label: operator || 'Unknown', 
      color: '#6B7280', 
      bgColor: 'bg-gray-500' 
    };
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      
      return date.toLocaleString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // You can add a notification here if you want
      console.log('Copied to clipboard:', text);
    });
  };

  const exportDetails = () => {
    if (!transfer) return;
    
    const data = {
      'Reference': transfer.reference,
      'Amount': `${transfer.amount} ${transfer.currency}`,
      'From': transfer.source_method,
      'To': transfer.target_method,
      'Status': transfer.status,
      'Creation Date': formatDate(transfer.created_at),
      'Merchant': transfer.merchant_code,
      'Transaction Type': transfer.transfer_type || 'N/A',
      'Method': transfer.method || 'N/A',
      'Source Operator': transfer.source_method || 'N/A',
      'Target Operator': transfer.target_method || 'N/A',
      'Initiator': transfer.initiated_by || 'N/A',
      'Processed By': transfer.approved_by || 'N/A',
      'Comment': transfer.rejected_reason || 'N/A'
    };

    const csvContent = Object.entries(data)
      .map(([key, value]) => `${key};${value}`)
      .join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transfer_${transfer.reference}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading transfer details...</p>
        </div>
      </div>
    );
  }

  if (!transfer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="h-8 w-8 text-rose-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Transfer not found</h2>
          <p className="text-slate-400 mb-6">
            The transfer with reference "{reference}" does not exist or has been deleted.
          </p>
          <button 
            onClick={() => navigate('/transfers')}
            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors flex items-center gap-2 mx-auto"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to transfers history
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(transfer.status);
  const typeConfig = getTypeConfig(transfer.transfer_type);
  const sourceOperatorConfig = getOperatorConfig(transfer.source_method);
  const targetOperatorConfig = getOperatorConfig(transfer.target_method);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 p-4 md:p-6">
      {/* Header with navigation */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/transfers')}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="hidden md:inline">Back to transfers</span>
            </button>
            <div className="h-6 w-px bg-white/10 hidden md:block"></div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Transfer Details</h1>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            
            <button
              onClick={exportDetails}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors"
            >
              <Download className="h-4 w-4" />
              <span className="hidden md:inline">Export</span>
            </button>
          </div>
        </div>
        
        {/* Transfer header */}
        <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-400">Reference:</span>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-lg font-bold text-white bg-slate-800/50 px-3 py-1 rounded-lg">
                    {transfer.reference}
                  </code>
                  <button
                    onClick={() => transfer.reference && copyToClipboard(transfer.reference)}
                    className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    title="Copy reference"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="text-slate-400 text-sm">
                Created on {formatDate(transfer.created_at)}
              </p>
            </div>
            
            <div className="flex flex-col md:items-end gap-3">
              <div className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full ${statusConfig.bg} ${statusConfig.border} border`}>
                {statusConfig.icon}
                <span className={`font-medium ${statusConfig.text}`}>
                  {transfer.status}
                </span>
              </div>
              
              <div className="text-3xl font-bold text-white">
                {formatCurrency(transfer.amount, transfer.currency)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Information grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Main information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Parties card */}
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-emerald-400" />
              Involved Parties
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Sender */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    <span className="text-sm font-medium text-slate-400">From</span>
                  </div>
                  <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full">
                    Sender
                  </span>
                </div>
                
                <div className="pl-4 space-y-3">
                  <div>
                    <p className="text-sm text-slate-400">Name/Account</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-lg font-medium text-white">{transfer.source_method}</p>
                      <button
                        onClick={() => copyToClipboard(transfer.source_method)}
                        className="p-1 text-slate-400 hover:text-white"
                        title="Copy"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  
                  {transfer.initiated_by && (
                    <div>
                      <p className="text-sm text-slate-400">Initiator</p>
                      <p className="text-white">{transfer.initiated_by}</p>
                    </div>
                  )}
                  
                  {transfer.source_method && (
                    <div>
                      <p className="text-sm text-slate-400">Source Operator</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: sourceOperatorConfig.color }}
                        />
                        <span className="text-white">{sourceOperatorConfig.label}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Recipient */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="text-sm font-medium text-slate-400">To</span>
                  </div>
                  <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full">
                    Recipient
                  </span>
                </div>
                
                <div className="pl-4 space-y-3">
                  <div>
                    <p className="text-sm text-slate-400">Name/Account</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-lg font-medium text-white">{transfer.target_method}</p>
                      <button
                        onClick={() => transfer.target_method && copyToClipboard(transfer.target_method)}
                        className="p-1 text-slate-400 hover:text-white"
                        title="Copy"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  
                  {transfer.approved_by && (
                    <div>
                      <p className="text-sm text-slate-400">Processed By</p>
                      <p className="text-white">{transfer.approved_by}</p>
                    </div>
                  )}
                  
                  {transfer.target_method && (
                    <div>
                      <p className="text-sm text-slate-400">Target Operator</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: targetOperatorConfig.color }}
                        />
                        <span className="text-white">{targetOperatorConfig.label}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Financial details card */}
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <BanknoteIcon className="h-5 w-5 text-emerald-400" />
              Financial Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-slate-400">Amount</p>
                <p className="text-2xl font-bold text-white mt-2">
                  {formatCurrency(transfer.amount, transfer.currency)}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-slate-400">Currency</p>
                <div className="inline-flex items-center px-3 py-1.5 bg-slate-800/50 rounded-full mt-2">
                  <span className="text-white font-medium">{transfer.currency}</span>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-slate-400">Transaction Type</p>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full mt-2 ${typeConfig.color}`}>
                  {typeConfig.icon}
                  <span className="text-sm font-medium">{typeConfig.label}</span>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-slate-400">Payment Method</p>
                <p className="text-white font-medium mt-2">
                  {transfer.method ? transfer.method.replace('_', ' ').toUpperCase() : 'N/A'}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-slate-400">Merchant</p>
                <div className="flex items-center gap-2 mt-2">
                  <code className="font-mono text-white bg-slate-800/50 px-2 py-1 rounded">
                    {transfer.merchant_code}
                  </code>
                  <button
                    onClick={() => copyToClipboard(transfer.merchant_code)}
                    className="p-1 text-slate-400 hover:text-white"
                    title="Copy"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              
              {transfer.merchant_type && (
                <div>
                  <p className="text-sm text-slate-400">Bank Account ID</p>
                  <p className="text-white font-medium mt-2">{transfer.merchant_type}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column - Additional information */}
        <div className="space-y-6">
          {/* Dates card */}
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-emerald-400" />
              Dates
            </h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-400">Created On</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-white">{formatDate(transfer.created_at)}</p>
                  <button
                    onClick={() => copyToClipboard(formatDate(transfer.created_at))}
                    className="p-1 text-slate-400 hover:text-white"
                    title="Copy"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              
              {transfer.updated_at && (
                <div>
                  <p className="text-sm text-slate-400">Updated On</p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-white">{formatDate(transfer.updated_at)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Comments card */}
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-400" />
              Comment
            </h2>
            
            <div className="bg-slate-800/50 rounded-xl p-4 min-h-[100px]">
              {transfer.rejected_reason ? (
                <p className="text-white whitespace-pre-wrap">{transfer.rejected_reason}</p>
              ) : (
                <p className="text-slate-400 italic">No comment</p>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Transfer ID at the bottom */}
      {transfer.id && (
        <div className="mt-6 text-center">
          <p className="text-sm text-slate-400">
            Transfer ID: <span className="font-mono text-slate-300">{transfer.id}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default TransferDetails;